/**
 * Stream ticket lifetime rules.
 *
 * A run socket upgrade presents a short-lived HMAC ticket
 * (`STREAM_TICKET_TTL_SECONDS = 60` in `packages/control-plane/src/auth-routes.ts`).
 * Two things must hold for the live stream to survive past that window: the ticket
 * is refreshed *before* it expires, and a reconnect never presents a stale one.
 * Keeping the arithmetic here (rather than inline in the screen) makes both
 * testable without a DOM.
 */

/** Refresh this far before expiry so an in-flight reconnect is never stale. */
export const STREAM_TICKET_REFRESH_MARGIN_MS = 10_000;

/** Never poll the ticket endpoint faster than this, even for a broken `expiresAt`. */
export const STREAM_TICKET_MIN_REFRESH_MS = 5_000;

/**
 * Milliseconds to wait before fetching a fresh ticket.
 *
 * Deliberately refreshed *before* the ticket actually expires; clamped to a floor
 * so a malformed or already-passed `expiresAt` cannot spin the endpoint.
 */
export function streamTicketRefreshDelayMs(expiresAt: string, nowMs: number): number {
  const expiry = Date.parse(expiresAt);
  if (!Number.isFinite(expiry)) return STREAM_TICKET_MIN_REFRESH_MS;
  return Math.max(STREAM_TICKET_MIN_REFRESH_MS, expiry - nowMs - STREAM_TICKET_REFRESH_MARGIN_MS);
}

/** Whether a ticket is still valid at `nowMs`. */
export function isStreamTicketCurrent(expiresAt: string, nowMs: number): boolean {
  const expiry = Date.parse(expiresAt);
  return Number.isFinite(expiry) && expiry > nowMs;
}
